<template>
  <c-box overflow="hidden" :pl="large ? { base: 0, md: 'xs' } : '0'">
    <c-box :d="{ base: 'block', md: 'none' }" mb="sm">
      <c-flex align="center">
        <c-flex width="100%" max-width="22rem">
          <c-input
            id="globalSearchInputForMobile"
            position="relative"
            px="2xs"
            h="8"
            :is-full-width="true"
            bg="transparent"
            color="brand.purple"
            :_placeholder="{ color: 'brand.purple' }"
            border-radius="0"
            border-width="0px"
            border-bottom-width="1px"
            border-bottom-color="brand.purple"
            focus-border-color="transparent"
            v-model="data.searchTerm"
            type="text"
            autocomplete="off"
            :placeholder="$t('globalSearch.placeholder')"
            @keydown="onSearchTermUpdated(data.searchTerm)"
            @keyup="onSearchTermUpdated(data.searchTerm)"
            @blur="onSearchTermUpdated(data.searchTerm)"
            @keydown.esc="onEscPressed"
            :_hover="{ borderBottomColor: 'brand.green' }"
            :_focus="{ borderBottomColor: 'brand.green' }"
            :_active="{ borderBottomColor: 'brand.purple' }"
          />
        </c-flex>
        <c-flex>
          <c-icon name="search" size="2rem" ml="md" color="brand.blue" />
        </c-flex>
      </c-flex>
    </c-box>

    <c-box v-if="large" :d="{ base: 'none', md: 'block' }" mb="4xl" ml="lg">
      <c-heading as="h1" size="2xl" fontWeight="400"
        >{{ $t("globalSearch.searchResultsFor") }} &#x201D;{{ searchTerm }}&#x201D;</c-heading
      >
    </c-box>

    <c-box
      :d="{ base: 'none', md: 'block' }"
      :w="{ base: '100%', lg: '50%', '2xl': '40%' }"
      float="left"
      px="lg"
      mb="xl"
    >
      <c-heading as="h3" fontSize="xl" fontWeight="400" mb="xl" ml="lg"
        >{{ $t("globalSearch.results.product") }} (<c-text
          v-if="!searchResults.product.searching"
          as="span"
          >{{ searchResults.product.count }}</c-text
        >
        <Spinner v-else size="sm"></Spinner>)
      </c-heading>
      <GlobalSearchResultList :large="large" :results="searchResults.product" />
    </c-box>

    <c-box
      :d="{ base: 'none', md: 'block' }"
      :w="{ base: '100%', lg: '50%', '2xl': '60%' }"
      float="left"
      px="lg"
      mb="xl"
    >
      <c-tabs
        variant="soft-rounded"
        :defaultIndex="activeTabIndex"
        :index="activeTabIndex"
        :isManual="true"
        @change="onTabChanged"
      >
        <c-tab-list
          d="flex"
          mb="xl"
          ml="lg"
          overflow="hidden"
          v-chakra="{
            button: {
              display: 'block',
              overflow: 'hidden',
              overflowWrap: 'normal',
              textOverflow: 'ellipsis',
              fontSize: 'sm',
              mx: '-xs',
            },
            '& button[aria-selected=true], & button[data-selected]': {
              color: 'white',
              bg: 'brand.purple',
              borderColor: 'brand.purple',
            },
          }"
          px="xs"
        >
          <c-tab
            v-for="kind in ['supportArticle', 'marketingMaterial', 'newsAndBulletin', 'event']"
            v-bind:key="kind"
            px="2xl"
            py="xs"
            fontWeight="normal"
            lineHeight="1.2"
            :isDisabled="searchResults[kind].count == 0"
            :title="$t(`globalSearch.results.${kind}`)"
            >{{ $t(`globalSearch.results.${kind}`) }} (<c-text
              v-if="!searchResults[kind].searching"
              as="span"
              >{{ searchResults[kind].count }}</c-text
            >
            <Spinner v-else size="xs"></Spinner>)
          </c-tab>
        </c-tab-list>
        <c-tab-panels>
          <c-tab-panel
            v-for="kind in ['supportArticle', 'marketingMaterial', 'newsAndBulletin', 'event']"
            v-bind:key="kind"
          >
            <GlobalSearchResultList :large="large" :results="searchResults[kind]" />
          </c-tab-panel>
        </c-tab-panels>
      </c-tabs>
    </c-box>

    <c-accordion
      :d="{ base: 'block', md: 'none' }"
      :allowMultiple="true"
      v-chakra="{
        '.accordion-header': { fontSize: '2xl', mr: 'sm' },
        button: { background: 'none !important' },
      }"
    >
      <c-accordion-item
        v-for="kind in [
          'product',
          'supportArticle',
          'marketingMaterial',
          'newsAndBulletin',
          'event',
        ]"
        v-bind:key="kind"
        :isDisabled="searchResults[kind].count == 0"
        border-top-width="0"
        border-bottom-width="0!important"
        v-slot="{ isExpanded }"
      >
        <c-accordion-header
          textAlign="left"
          px="0"
          pt="0"
          :pb="isExpanded ? 0 : 'lg'"
          fontSize="xl"
          lineHeight="1.4"
        >
          <c-box d="inline-block" verticalAlign="middle" maxWidth="calc(100% - 2.5rem)"
            >{{ $t(`globalSearch.results.${kind}`) }} (<template
              v-if="!searchResults[kind].searching"
              >{{ searchResults[kind].count }}</template
            >
            <Spinner v-else size="xs"></Spinner>)</c-box
          >
          <c-accordion-icon name="chevron_down" ml="md" fontSize="1.5rem" />
        </c-accordion-header>
        <c-accordion-panel
          v-if="searchResults[kind].count > 0"
          p="0"
          mx="-xs"
          overflow="hidden"
          :d="isExpanded ? 'block' : 'none'"
        >
          <GlobalSearchResultList :large="large" :results="searchResults[kind]" />
        </c-accordion-panel>
      </c-accordion-item>
    </c-accordion>
  </c-box>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import _ from "lodash";
import { mapState } from "vuex";
import {
  CAccordion,
  CAccordionHeader,
  CAccordionIcon,
  CAccordionItem,
  CAccordionPanel,
  CBox,
  CFlex,
  CHeading,
  CIcon,
  CInput,
  CTab,
  CTabList,
  CTabPanel,
  CTabPanels,
  CTabs,
  CText,
} from "@chakra-ui/vue";
import { IGlobalSearchState } from "@/store/globalSearchModule";
import GlobalSearchResultList from "@/components/globalSearch/GlobalSearchResultList.vue";
import Spinner from "@/components/Spinner.vue";

export default defineComponent({
  name: "GlobalSearchComponent",
  components: {
    CIcon,
    CBox,
    CFlex,
    CText,
    CHeading,
    CInput,
    CTabs,
    CTabList,
    CTab,
    CTabPanels,
    CTabPanel,
    CAccordion,
    CAccordionHeader,
    CAccordionIcon,
    CAccordionItem,
    CAccordionPanel,
    GlobalSearchResultList,
    Spinner,
  },
  props: {
    large: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      data: {
        searchTerm: "",
        lastSearchTerm: "x",
      },
      typing: false,
      activeTabIndex: 0,
    };
  },
  computed: {
    ...mapState("globalSearch", {
      searchTerm: (state: unknown) => (state as IGlobalSearchState).searchTerm,
      searchResults: (state: unknown) => (state as IGlobalSearchState).searchResults,
    }),
  },
  watch: {
    searchTerm(newTerm) {
      this.onSearchTermUpdatedInStore();
    },
    searchResults: {
      handler() {
        this.onSearchResultsUpdated();
      },
      deep: true,
    },
  },
  mounted() {
    this.data.searchTerm = this.searchTerm;
    this.data.lastSearchTerm = this.searchTerm;
    this.onSearchResultsUpdated(); // Active tab autoselect
  },
  methods: {
    onSearchTermUpdated(term) {
      if (term.trim() !== (this.data.lastSearchTerm || "").trim()) {
        this.typing = true;

        // The following prevents wrong/old results to be displayed
        this.$store.dispatch("globalSearch/updateSearchTermWithoutSearching", term);

        this.search(term);
      }
    },
    onSearchTermUpdatedInStore() {
      if (this.data.searchTerm.trim() !== this.searchTerm) {
        this.data.searchTerm = this.searchTerm;
      }
    },
    search: _.debounce(function (this: any, term: string) {
      if (term !== this.data.lastSearchTerm) {
        this.data.lastSearchTerm = term;
        this.$store.dispatch("globalSearch/updateSearchTerm", term);
        this.typing = false;
      }
    }, 400),
    onEscPressed() {
      if (this.data.searchTerm === "") {
        this.$store.commit("globalSearch/setOverlayVisibility", false);
      } else {
        this.data.searchTerm = "";
      }
    },
    onSearchResultsUpdated() {
      const assetsByTabIndex = ["supportArticle", "marketingMaterial", "newsAndBulletin", "event"];

      const searching = assetsByTabIndex.some((asset) => this.searchResults[asset].searching);
      if (searching) {
        // Don't auto-switch tabs during search is running in some tab
        return;
      }

      const visibleResult = this.searchResults[assetsByTabIndex[this.activeTabIndex]];
      if (visibleResult.count === 0) {
        const firstNonEmptyAsset = assetsByTabIndex.find(
          (asset) => this.searchResults[asset].count > 0
        );
        this.activeTabIndex = assetsByTabIndex.indexOf(firstNonEmptyAsset || "supportArticle");
      }
    },
    onTabChanged(idx) {
      this.activeTabIndex = idx;
    },
  },
});
</script>
